/**
 * Hidden Gems tab — specialist convergence signal ("best ideas" of sector
 * specialists via ia_skew) with right-tail analysis and a live screener.
 */
import React from "react";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { AlphaVal, PanelSummary, RunBar, SigBadge, StatCard } from "./common";

const Th = ({ children, right }) => (
  <th className={`py-1.5 px-2 font-medium text-muted-foreground ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);
const Td = ({ children, right }) => (
  <td className={`py-1.5 px-2 ${right ? "text-right" : ""}`}>{children}</td>
);

const IcCell = ({ ic }) =>
  ic ? (
    <span className="font-mono text-xs">
      {ic.mean_ic ?? "-"} <span className="text-muted-foreground">(t={ic.nw_tstat ?? "-"})</span>
    </span>
  ) : <span className="text-muted-foreground">-</span>;

const SpreadCell = ({ sp }) =>
  sp ? (
    <span className="text-xs">
      <AlphaVal value={sp.mean_pct} />{" "}
      <span className="text-muted-foreground font-mono">(t={sp.nw_tstat ?? "-"})</span>
    </span>
  ) : <span className="text-muted-foreground">-</span>;

const H_LABELS = { 63: "3M", 126: "6M", 252: "12M" };

/* ---------------- Ingredients ---------------- */

const Ingredients = ({ ing }) => {
  if (!ing) return null;
  return (
    <Card data-testid="gems-ingredients">
      <CardHeader>
        <CardTitle className="text-base">Signal Ingredients — quintile spreads & ICs</CardTitle>
        <CardDescription>
          Each building block of the gem score, tested standalone vs sector×cap alpha.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <Th>Ingredient</Th>
                <Th right>3M Q5–Q1</Th>
                <Th right>6M Q5–Q1</Th>
                <Th right>12M Q5–Q1</Th>
                <Th right>3M IC</Th>
                <Th right>Sig (3M)</Th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(ing).map(([feat, b]) => (
                <tr key={feat}
                    className={`border-b border-border/50 ${feat === "gem_score" ? "bg-primary/5 font-medium" : ""}`}
                    data-testid={`gems-ing-${feat}`}>
                  <Td>{b.label}</Td>
                  {[63, 126, 252].map((h) => (
                    <Td key={h} right><SpreadCell sp={b[`sector_${h}`]?.spread} /></Td>
                  ))}
                  <Td right><IcCell ic={b.sector_63?.ic} /></Td>
                  <Td right><SigBadge t={b.sector_63?.spread?.nw_tstat} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ---------------- Convergence event study ---------------- */

const Convergence = ({ conv }) => {
  if (!conv?.horizons) return null;
  return (
    <Card data-testid="gems-convergence">
      <CardHeader>
        <CardTitle className="text-base">Specialist Convergence — 0 vs 1 vs 2+ specialists buying</CardTitle>
        <CardDescription>{conv.definition}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <Th>Horizon</Th>
                <Th right>0 specialists</Th>
                <Th right>1 specialist</Th>
                <Th right>2+ specialists</Th>
                <Th right>Gap (2+ vs 0)</Th>
                <Th right>Sig</Th>
              </tr>
            </thead>
            <tbody>
              {[63, 126, 252].map((h) => {
                const e = conv.horizons[`h${h}`];
                if (!e) return null;
                const gap = e.gap_2plus_vs_zero;
                return (
                  <tr key={h} className="border-b border-border/50" data-testid={`gems-conv-h${h}`}>
                    <Td><span className="font-medium">{H_LABELS[h]}</span></Td>
                    {["zero", "one", "2plus"].map((g) => (
                      <Td key={g} right>
                        {e.groups?.[g] ? <AlphaVal value={e.groups[g].mean_pct} /> : "-"}
                      </Td>
                    ))}
                    <Td right>
                      {gap ? (
                        <><AlphaVal value={gap.mean_pct} />{" "}
                        <span className="text-muted-foreground font-mono text-xs">
                          (t={gap.nw_tstat}, {gap.pct_positive}% +)
                        </span></>
                      ) : "-"}
                    </Td>
                    <Td right><SigBadge t={gap?.nw_tstat} /></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {conv.breadth_controlled_63d && (
          <div className="mt-3 border rounded-lg p-3" data-testid="gems-conv-controlled">
            <div className="text-sm font-medium mb-1">Breadth-controlled (3M): convergence gap within breadth terciles</div>
            <div className="flex flex-wrap gap-4 text-xs">
              {Object.entries(conv.breadth_controlled_63d).map(([k, v]) => (
                <div key={k}>
                  <span className="text-muted-foreground">{k.replace("_", " ")}: </span>
                  <AlphaVal value={v.gap_2plus_vs_zero_pct} />{" "}
                  <span className="text-muted-foreground font-mono">(t={v.nw_tstat})</span>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">{conv.breadth_controlled_note}</div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const GemDeciles = ({ deciles, tail }) => {
  if (!deciles && !tail) return null;
  return (
    <Card data-testid="gems-deciles">
      <CardHeader>
        <CardTitle className="text-base">Gem Score — deciles & the right tail</CardTitle>
        <CardDescription>
          The philosophy is very high alpha, so we test the TAIL: how often does a top-decile
          gem deliver an extreme positive outcome vs the rest of the universe?
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <Th>Horizon</Th>
                <Th right>D10–D1 spread</Th>
                <Th right>Sig</Th>
                <Th right>Tail threshold</Th>
                <Th right>P(tail) top decile</Th>
                <Th right>P(tail) rest</Th>
                <Th right>Lift</Th>
              </tr>
            </thead>
            <tbody>
              {[63, 126, 252].map((h) => {
                const d = deciles?.[`h${h}`];
                const t = tail?.[`h${h}`];
                if (!d && !t) return null;
                return (
                  <tr key={h} className="border-b border-border/50" data-testid={`gems-tail-h${h}`}>
                    <Td><span className="font-medium">{H_LABELS[h]}</span></Td>
                    <Td right><SpreadCell sp={d?.spread} /></Td>
                    <Td right><SigBadge t={d?.spread?.nw_tstat} /></Td>
                    <Td right><span className="font-mono text-xs">&gt;+{t?.threshold_pct ?? "-"}%</span></Td>
                    <Td right><span className="font-mono text-xs">{t?.p_top_decile_pct ?? "-"}%</span></Td>
                    <Td right><span className="font-mono text-xs">{t?.p_rest_pct ?? "-"}%</span></Td>
                    <Td right>
                      <span className="font-mono text-xs font-semibold">{t?.lift ?? "-"}x</span>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {tail?.h252 && (
          <div className="text-xs text-muted-foreground">
            Top-decile 12M distribution: median <AlphaVal value={tail.h252.top_decile_median_pct} />,
            90th percentile <AlphaVal value={tail.h252.top_decile_p90_pct} /> ({tail.h252.n_top?.toLocaleString()} obs).
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ---------------- Cross-sections ---------------- */

const CrossSections = ({ opt, cap }) => {
  if (!opt && !cap) return null;
  return (
    <Card data-testid="gems-cross">
      <CardHeader>
        <CardTitle className="text-base">Gems × Options State · Gems × Cap Bucket</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <StatCard label={opt?.scope || "Top gems: Quiet vs Hot options (3M)"} testId="gems-x-options">
          {opt ? (
            <div className="text-sm">
              Quiet <AlphaVal value={opt.quiet_mean_pct} /> vs Hot <AlphaVal value={opt.hot_mean_pct} />
              {" → "}<AlphaVal value={opt.quiet_minus_hot_pct} />{" "}
              <span className="text-muted-foreground font-mono text-xs">(Welch t={opt.welch_t})</span>{" "}
              <SigBadge t={opt.welch_t} />
            </div>
          ) : <span className="text-muted-foreground text-sm">n/a</span>}
        </StatCard>
        <StatCard label="Gem quintile spread by cap bucket" testId="gems-x-cap">
          {cap ? (
            <div className="text-xs space-y-1">
              {Object.entries(cap).map(([bucket, e]) => (
                <div key={bucket}>
                  <span className="font-medium">{bucket === "big" ? "Big caps" : "Mid/Small"}</span>:{" "}
                  3M <SpreadCell sp={e.h63} />{e.h126 && <> · 6M <SpreadCell sp={e.h126} /></>}
                </div>
              ))}
            </div>
          ) : <span className="text-muted-foreground text-sm">n/a</span>}
        </StatCard>
      </CardContent>
    </Card>
  );
};

/* ---------------- Live screener ---------------- */

export const Screener = ({ scr }) => {
  if (!scr?.rows?.length) return null;
  return (
    <Card data-testid="gems-screener">
      <CardHeader>
        <CardTitle className="text-base">Live Hidden Gems Screener — as of {scr.as_of}</CardTitle>
        <CardDescription>
          Top {scr.rows.length} names by gem score, with the specialists behind them.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b">
                <Th>#</Th>
                <Th>Symbol</Th>
                <Th right>Gem Score</Th>
                <Th right>Specialists (2Q)</Th>
                <Th right>Conviction</Th>
                <Th right>Max ia_skew</Th>
                <Th right># Funds</Th>
                <Th>Regime</Th>
                <Th>Options</Th>
                <Th>Top Specialists</Th>
              </tr>
            </thead>
            <tbody>
              {scr.rows.map((r, i) => (
                <tr key={r.symbol} className="border-b border-border/50"
                    data-testid={`gems-row-${r.symbol}`}>
                  <Td><span className="text-muted-foreground text-xs">{i + 1}</span></Td>
                  <Td>
                    <span className="font-semibold">{r.symbol}</span>{" "}
                    <Badge variant="outline" className="text-[9px] ml-1">
                      {r.cap_bucket === "big" ? "Big" : "Mid/Small"}
                    </Badge>
                  </Td>
                  <Td right><span className="font-mono text-xs font-semibold">{r.gem_score}</span></Td>
                  <Td right><span className="font-mono text-xs">{r.n_specialist_buys_2q}</span></Td>
                  <Td right><span className="font-mono text-xs">{r.n_specialist_conviction_2q}</span></Td>
                  <Td right><span className="font-mono text-xs">{r.max_holder_iaskew ?? "-"}</span></Td>
                  <Td right><span className="font-mono text-xs">{r.n_funds ?? "-"}</span></Td>
                  <Td><span className="text-xs">{r.regime ?? "-"}</span></Td>
                  <Td>
                    {r.options_state ? (
                      <Badge variant="outline"
                             className={`text-[10px] border-0 ${
                               r.options_state === "Quiet" ? "bg-emerald-600/20 text-emerald-500"
                               : r.options_state === "Hot" ? "bg-red-600/20 text-red-400"
                               : "bg-muted text-muted-foreground"}`}>
                        {r.options_state}
                      </Badge>
                    ) : <span className="text-muted-foreground text-xs">-</span>}
                  </Td>
                  <Td>
                    <span className="text-[11px] text-muted-foreground">
                      {(r.top_specialists || []).map((f) => f.name).join(" · ") || "-"}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

/* ---------------- Tab ---------------- */

export const HiddenGemsTab = ({ signal }) => {
  const { result, job, running, run } = signal;
  const res = result?.result;

  return (
    <div className="space-y-4" data-testid="gems-tab">
      <Card>
        <CardHeader>
          <CardTitle>Hidden Gems — specialist convergence</CardTitle>
          <CardDescription>
            Sector specialists (high ia_skew funds) converging on a small number of names is the
            "best ideas" setup. This lab measures whether specialist convergence × holder skill ×
            nowcast gradient × low breadth finds very-high-alpha names — mean AND tail.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RunBar result={result} job={job} running={running} onRun={run} testId="gems-runbar" />
          {res && (
            <>
              <PanelSummary panel={res.panel} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Buy events (PIT)">{res.coverage?.buy_events?.toLocaleString()}</StatCard>
                <StatCard label="With ia_skew">{res.coverage?.events_with_iaskew?.toLocaleString()}</StatCard>
                <StatCard label="Specialist events">{res.coverage?.specialist_events?.toLocaleString()}</StatCard>
                <StatCard label="Skill snapshots">{res.coverage?.skill_snapshots}</StatCard>
              </div>
              <div className="text-xs text-muted-foreground border-l-2 border-primary/50 pl-3"
                   data-testid="gems-definitions">
                <span className="font-medium">Gem score:</span> {res.definitions?.gem_score}.{" "}
                <span className="font-medium">Specialist:</span> {res.definitions?.specialist}.{" "}
                {res.definitions?.point_in_time}.
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {res && (
        <>
          <Ingredients ing={res.ingredients} />
          <Convergence conv={res.convergence} />
          <GemDeciles deciles={res.gem_deciles} tail={res.right_tail} />
          <CrossSections opt={res.gems_x_options} cap={res.gems_x_cap} />
          <Screener scr={res.screener} />
        </>
      )}
    </div>
  );
};
